import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Building, Mail, Lock, LockKeyhole } from "lucide-react"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import Logo from "@/components/layout/logo"
import { usePlatformParams } from "@/hooks/use-platform-params"
import { signup } from "@/api/auth"
import type { LoginCodeResponse, SignupCredentials } from "@/types/auth"
import { toast } from 'sonner'
import ROUTES from "@/config/routes"
import type { ApiResponse } from "@/types/api"

export function SignupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { platformParams, createUrlWithParams } = usePlatformParams();
  const navigate = useNavigate();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (password.length < 6) {
      toast.error('비밀번호는 6자리 이상이어야 합니다.');
      return;
    }

    setIsLoading(true);

    try {
      // URL 파라미터에서 platform_mall_id 가져오기, 없으면 기본값 사용
      const platformMallId = platformParams.platform_mall_id || 'none';
      
      const credentials: SignupCredentials = {
        platformMallId,
        email,
        password
      };

      const platform = platformParams.platform || 'cafe24';
      const response: ApiResponse<LoginCodeResponse> = await signup(credentials, platform);
      console.log('회원가입 응답:', response);

      // API Response가 성공적이면 code가 200이거나 2xx 범위
      if (response.code && response.code >= 200 && response.code < 300) {
        toast.success('회원가입이 완료되었습니다.');
        // redirectUrl로 리다이렉트
        if (response.data.redirectUrl) {
          // 서버에서 받은 redirectUrl에 플랫폼 파라미터가 없다면 추가
          const redirectUrl = new URL(response.data.redirectUrl);
          if (platformParams.platform && !redirectUrl.searchParams.has('platform')) {
            redirectUrl.searchParams.set('platform', platformParams.platform);
          }
          if (platformParams.platform_mall_id && !redirectUrl.searchParams.has('platform_mall_id')) {
            redirectUrl.searchParams.set('platform_mall_id', platformParams.platform_mall_id);
          }
          window.location.href = redirectUrl.toString();
        } else {
          // fallback: auth 페이지로 이동
          navigate(createUrlWithParams('/auth'));
        }
      } else if (response.code === 2001) {
        toast.error('이미 가입된 이메일입니다.');
      } else {
        toast.error(response.message || '회원가입에 실패했습니다.');
      }
    } catch (error) {
      console.error('회원가입 오류:', error);
      toast.error('회원가입 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>
          <CardTitle className="text-2xl">계정 생성</CardTitle>
          <CardDescription>
            임직원 관리 시스템에 가입하세요
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">이메일</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="이메일을 입력하세요"
                  className="pl-10"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">비밀번호</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="비밀번호를 입력하세요 (6자리 이상)"
                  className="pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">비밀번호 확인</Label>
              <div className="relative">
                <LockKeyhole className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="비밀번호를 다시 입력하세요"
                  className="pl-10"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "회원가입 중..." : "회원가입"}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-muted-foreground">
            이미 서비스 계정이 있으신가요?{" "}
            <Link
              className="text-primary hover:text-primary/80 underline"
              to={createUrlWithParams(ROUTES.SIGNIN)}
            >
              로그인하기
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}